import {
  ModalConfirmAction,
  convertEventSelectToTarget,
  DataTable,
  FormFeedBack,
  IOptions,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import {
  AddPersonToGroupUseCaseInterface,
  GetAllPersonsFromGroupUseCaseInterface,
  GetAllProfessionalsNotPaginatedUseCaseInterface,
  RemovePersonFromGroupUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedGroupCoordinatorsListData,
  loadSelectedGroupCoordinatorsListData,
} from 'presentation/config/store/groups/group-settings-slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, CardBody, Col, Form, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsGroupSettingsPersonsFromGroupList } from '../../../common/columns/group-settings/columns-group-settings-persons-from-group-list';

interface IProps {
  groupId: string;
}

const statusInitialStates = {
  confirmRemovalFromGroupModalBtnsStatus: STATUSPAGE.DONE,
  importCoordinatorsFromAnotherGroupModalBtnsStatus: STATUSPAGE.DONE,
  coordinatorRemoteSelectInputStatus: STATUSPAGE.DONE,
  addCoordinatorToGroupBtnStatus: STATUSPAGE.DONE,
  coordinatorsFromGroupListStatus: STATUSPAGE.DONE,
};

export const CoordinatorsTabContent = ({ groupId }: IProps) => {
  // Hooks
  const { professionalService, groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const [
    isConfirmRemovalFromGroupModalOpen,
    setIsConfirmRemovalFromGroupModalOpen,
  ] = useState(false);
  const [
    selectedCoordinatorFromGroupRelationId,
    setSelectedCoordinatorFromGroupRelationId,
  ] = useState<string | null>(null);
  const [statusStates, setStatusStates] = useState(statusInitialStates);
  const { selectedGroupCoordinatorsListData } = useAppSelector(
    (state) => state.groupSettings,
  );
  const [coordinatorSelectOptions, setCoordinatorSelectOptions] =
    useState<GetAllProfessionalsNotPaginatedUseCaseInterface.Result>(null);
  const coordinatorOptions: IOptions[] = useMemo(() => {
    return (
      coordinatorSelectOptions?.data?.map((coordinator) => {
        const { cpf, name, id } = coordinator;
        return {
          label: `${cpf} - ${name}`,
          value: id,
        };
      }) || []
    );
  }, [coordinatorSelectOptions]);

  // Methods
  const changeStatusState = useCallback(
    <T extends keyof typeof statusInitialStates>(key: T, value: STATUSPAGE) => {
      setStatusStates((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const toggleConfirmRemovalFromGroupModal = useCallback(
    () => setIsConfirmRemovalFromGroupModalOpen((prevState) => !prevState),
    [],
  );

  const requestGetAllCoordinatorsFromGroup = useCallback(
    async (params: GetAllPersonsFromGroupUseCaseInterface.Params) => {
      changeStatusState('coordinatorsFromGroupListStatus', STATUSPAGE.LOADING);
      await groupService
        .getAllPersonsFromGroup(params)
        .then((res) => dispatch(loadSelectedGroupCoordinatorsListData(res)))
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearSelectedGroupCoordinatorsListData());
        })
        .finally(() =>
          changeStatusState('coordinatorsFromGroupListStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestGetAllCoordinatorsForSelectOptions = useCallback(
    async (params: GetAllProfessionalsNotPaginatedUseCaseInterface.Params) => {
      changeStatusState(
        'coordinatorRemoteSelectInputStatus',
        STATUSPAGE.LOADING,
      );
      await professionalService
        .getAllProfessionalsNotPaginated(params)
        .then((res) => setCoordinatorSelectOptions(res))
        .catch((err) => {
          notifyError(err.message);
          setCoordinatorSelectOptions(null);
        })
        .finally(() =>
          changeStatusState(
            'coordinatorRemoteSelectInputStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestAddCoordinatorToGroup = useCallback(
    async (params: AddPersonToGroupUseCaseInterface.Params) => {
      changeStatusState('addCoordinatorToGroupBtnStatus', STATUSPAGE.LOADING);
      await groupService
        .addPersonToGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllCoordinatorsFromGroup({
            groupId,
            personType: PROFILE_TYPE.COORDINATOR,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState('addCoordinatorToGroupBtnStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestRemoveCoordinatorFromGroup = useCallback(
    async (params: RemovePersonFromGroupUseCaseInterface.Params) => {
      changeStatusState(
        'confirmRemovalFromGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .removePersonFromGroup(params)
        .then((res) => notifySuccess(res.message))
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'confirmRemovalFromGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      coordinator: null,
    },

    validationSchema: Yup.object({
      coordinator: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        id: values.coordinator?.value,
        group: groupId,
        personType: PROFILE_TYPE.COORDINATOR,
      };

      requestAddCoordinatorToGroup(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllCoordinatorsFromGroup({
      groupId,
      personType: PROFILE_TYPE.COORDINATOR,
    });
  }, [groupId]);

  return (
    <>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <CardBody>
          <Row>
            <Col sm={10} className="mb-3">
              <SelectRemote
                isClearable
                name="coordinator"
                clearOptions={() => setCoordinatorSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setCoordinatorSelectOptions(null);
                    return;
                  }

                  if (searchString.length < 3) return;

                  requestGetAllCoordinatorsForSelectOptions({
                    profileType: PROFILE_TYPE.COORDINATOR,
                    query: searchString,
                  });
                }}
                inputId={'coordinator'}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('coordinator', e),
                  );
                }}
                onBlur={(e) => {
                  setCoordinatorSelectOptions(null);
                }}
                options={coordinatorOptions}
                placeholder={`Selecione um ${getLegends('COORDENADOR')}`}
                value={validation.values.coordinator}
                loading={
                  statusStates.coordinatorRemoteSelectInputStatus ===
                  STATUSPAGE.LOADING
                }
                isInvalid={
                  !!(
                    validation.touched?.coordinator &&
                    validation.errors?.coordinator
                  )
                }
                isDisabled={
                  statusStates.addCoordinatorToGroupBtnStatus ===
                  STATUSPAGE.LOADING
                }
              />
              <FormFeedBack
                error={String(validation.errors?.coordinator)}
                isInvalid={
                  !!(
                    validation.touched?.coordinator &&
                    validation.errors?.coordinator
                  )
                }
              />
            </Col>
            <Col sm={2} className="mb-3">
              <div className="d-flex justify-content-center">
                <Button
                  type="submit"
                  disabled={
                    statusStates.addCoordinatorToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  {statusStates.addCoordinatorToGroupBtnStatus ===
                    STATUSPAGE.LOADING && (
                    <Spinner size="sm">{['Carregando...']}</Spinner>
                  )}
                  Adicionar
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <DataTable
              noDataBg={noDataBg}
              actions={[
                {
                  id: 1,
                  name: 'remove',
                  label: 'Remover',
                  icon: 'ri-indeterminate-circle-fill',
                  classNameColorIcon: 'danger',
                  onPress: (cellProps) => {
                    setSelectedCoordinatorFromGroupRelationId(cellProps.id);
                    toggleConfirmRemovalFromGroupModal();
                  },
                },
              ]}
              canSearch
              columns={makeColumnsGroupSettingsPersonsFromGroupList()}
              data={selectedGroupCoordinatorsListData}
              loading={
                statusStates.coordinatorsFromGroupListStatus ===
                STATUSPAGE.LOADING
              }
            />
          </Row>
        </CardBody>
      </Form>

      <ModalConfirmAction
        modalTheme="danger"
        title="Deseja remover?"
        actionBtnLabel="Remover"
        isOpen={isConfirmRemovalFromGroupModalOpen}
        toggle={toggleConfirmRemovalFromGroupModal}
        onConfirm={() =>
          requestRemoveCoordinatorFromGroup({
            relationId: selectedCoordinatorFromGroupRelationId,
          })
        }
        loading={
          statusStates.confirmRemovalFromGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
      />
    </>
  );
};
